<template>
  <div>
    <div v-if="isLoading" class="d-flex justify-content-center mb-3 pt-4">
      <b-spinner style="width: 5rem; height: 5rem" variant="warning" label="Spinning"></b-spinner>
    </div>
    <div v-else>
      <div class="ml-3 mr-3" rounded="lg" id="overlay">
        <div class="row no-gutters mb-2 mt-4">
          <div class="col">
            <h1 class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-user-circle pr-1" data-test-hook="titleIcon" />
              <span data-test-hook="account-info-title-account-settings">{{ $t('xAccountInformation') }}</span>
            </h1>
          </div>
        </div>
        <div class="card" header-bg-variant="white">
          <div class="card-body">
            <div class="row mb-1">
              <div class="col-auto mb-1">
                <i class="far fa-user"></i>
                <strong class="ml-2" data-test-hook="account-holder-name-address-title-account-settings">
                  {{ $t('xAccountHolder') }}
                </strong>
              </div>
              <div class="col-auto">
                <popper trigger="hover" :options="{ placement: 'right' }">
                  <div class="popper">
                    {{ $t('xPrimaryBillingContactAndAddressInfo') }}
                  </div>
                  <i class="far fa-question-circle" slot="reference"></i>
                </popper>
              </div>
            </div>
            <div class="row">
              <div v-if="!accountInfo" class="d-flex justify-content-center mb-3 pt-1">
                <span
                  class="spinner-border spinner-border-xl text-warning"
                  style="width: 5rem; height: 5rem"
                  data-test-hook="LoadingSpinner"
                  label="Spinning"
                ></span>
              </div>
              <div v-if="accountInfo" class="col">
                <p data-test-hook="account-holder-name-account-settings">
                  {{ accountInfo.ownerFirstName + ' ' + accountInfo.ownerLastName }}
                </p>
                <p
                  v-if="accountInfo.ownerAddress.line1"
                  data-test-hook="account-holder-address-line-1-account-settings"
                >
                  {{ accountInfo.ownerAddress.line1 }}
                </p>
                <p
                  v-if="accountInfo.ownerAddress.line2"
                  data-test-hook="account-holder-address-line-2-account-settings"
                >
                  {{ accountInfo.ownerAddress.line2 }}
                </p>
                <p data-test-hook="account-holder-address-city-state-zip-account-settings">
                  {{
                    accountInfo.ownerAddress.city +
                      ', ' +
                      accountInfo.ownerAddress.region +
                      ' ' +
                      accountInfo.ownerAddress.postalCode
                  }}
                </p>
                <p>{{ accountInfo.ownerAddress.country }}</p>
                <p data-test-hook="account-holder-phone-number-account-settings">
                  {{ accountInfo.ownerPhoneNumber }}
                </p>
              </div>
            </div>
            <div class="row pb-4">
              <div class="col">
                <button
                  type="button"
                  class="btn btn-outline-warning mt-3"
                  @click="openEditAccountModal()"
                  :title="$t('xUpdateAccountInfo')"
                  data-test-hook="update-account-info-button-account-settings"
                  :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
                >
                  {{ $t('xUpdateAccountInfo') }}
                </button>
              </div>
            </div>
            <div class="row pt-4">
              <div class="col-auto mb-1">
                <i class="far fa-lock colorOrange"></i>
                <strong class="ml-2" data-test-hook="account-holder-login-title-account-settings">
                  {{ $t('xAccountLogin') }}
                </strong>
              </div>
              <div class="col-auto">
                <popper trigger="hover" :options="{ placement: 'right' }">
                  <div class="popper">
                    {{ $t('xEmailUsedToLoginAndRegisterMessage') }}
                  </div>
                  <i slot="reference" class="far fa-question-circle "></i>
                </popper>
              </div>
            </div>
            <div class="row">
              <div class="col-auto">
                <p v-if="accountProfile.identityEmail" data-test-hook="account-holder-login-email-account-settings">
                  {{ accountProfile.identityEmail }}
                </p>
                <p
                  v-else-if="impersonatedEmail && isImpersonating"
                  data-test-hook="account-holder-login-email-account-settings"
                >
                  {{ impersonatedEmail }}
                </p>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col">
                <button
                  type="button"
                  class="btn btn-outline-warning mt-3"
                  @click="onUpdateAccountEmailCLicked()"
                  :title="$t('xUpdateAccountEmail')"
                  data-test-hook="update-account-email-button-account-settings"
                  :disabled="billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
                >
                  {{ $t('xUpdateAccountEmail') }}
                  <i class="fas fa-external-link" />
                </button>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <hr class="mx-0 px-0" />
              </div>
            </div>
            <div class="row mb-4">
              <div class="col">
                <button
                  type="button"
                  class="btn btn-outline-danger rounded-pill mt-4"
                  @click="deleteAccount()"
                  :disabled="isImpersonating || billingMaintenanceFeatureFlagEnabled(currentAppConfig)"
                  :title="deleteAccountTitle()"
                  data-test-hook="delete-account-button-account-settings"
                >
                  <i class="fas fa-trash-alt colorOrange"></i>
                  {{ $t('xDeleteAccountBtn') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal modalId="unable-to-delete-account-modal" size="modal-lg">
        <ModalHeader :title="$t('xUnableToDeleteAccount')" icon="fa-regular fa-circle-exclamation" class="pb-0 pt-1">
          <button
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeModal('unable-to-delete-account-modal')"
            data-test-hook="unable-to-delete-account-x-button"
          >
            <i class="fas fa-times" />
          </button>
        </ModalHeader>
        <div>
          <ModalBody>
            <p
              v-if="!!preferredDealerEmail"
              v-html="$t('xDealerManagedDeleteAccountModalBody1', { email: this.preferredDealerEmail })"
              data-test-hook="modalBodyText1"
            />
            <p v-else v-html="$t('xDealerManagedDeleteAccountModalBodyGeneric')" data-test-hook="modalBodyText1" />
          </ModalBody>
        </div>
        <ModalFooter class="d-flex justify-content-center">
          <button
            class="btn btn-primary span9 btn-block"
            @click="closeModal('unable-to-delete-account-modal')"
            data-test-hook="modalDismissButton"
          >
            {{ $t('xDismiss') }}
          </button>
        </ModalFooter>
      </Modal>

      <EditAccountInfoModal
        :shouldSetModal="shouldSetModal"
        @shouldSetModal="setShouldSetModal()"
        :hasActiveSubscription="getIsSubscriptionActiveOrPendingChange()"
      />
      <DeleteAccountModal
        :deleteAccountModalInfo="deleteAccountModalInfo"
        @showConfirmDeleteAccountModal="showConfirmDeleteAccountModal"
      />
      <ConfirmDeleteAuth0AccountModal :accountProfile="accountProfile" />
      <DeleteAuth0AccountModal
        :accountProfile="accountProfile"
        @displayConfirmDeleteAuth0AccountModal="displayConfirmDeleteAuth0AccountModal"
      />
      <ConfirmDeleteAccountModal :confirmDeleteAccountModalInfo="confirmDeleteAccountModalInfo" />
      <DeleteAccountUnableToDeleteModal :deleteAccountUnableToDeleteModalInfo="deleteAccountUnableToDeleteModalInfo" />
      <Auth0MigrationFeatureDisabledModal />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { required, requiredIf, minLength, maxLength, email, numeric } from 'vuelidate/lib/validators'
import * as Constants from '@/constants'
import { validationMixin } from 'vuelidate'
import EditAccountInfoModal from '@/components/AccountSettings/EditAccountInfoModal'
import DeleteAccountModal from '@/components/AccountSettings/DeleteAccountModal'
import ConfirmDeleteAccountModal from '@/components/AccountSettings/ConfirmDeleteAccountModal'
import DeleteAccountUnableToDeleteModal from '@/components/AccountSettings/DeleteAccountUnableToDelete.vue'
import {
  useAuth0FeatureFlagEnabled,
  useAuth0AndB2CFeatureFlagEnabled,
  updateEmail,
  billingMaintenanceFeatureFlagEnabled
} from '../../utils/mixins'
import DeleteAuth0AccountModal from './DeleteAuth0AccountModal.vue'
import ConfirmDeleteAuth0AccountModal from './ConfirmDeleteAuth0AccountModal.vue'
import Auth0MigrationFeatureDisabledModal from './Auth0MigrationFeatureDisabledModal.vue'

const monthInRange = (value, vm) => value >= 1 && value <= 12

export default {
  components: {
    EditAccountInfoModal,
    DeleteAccountModal,
    ConfirmDeleteAccountModal,
    DeleteAccountUnableToDeleteModal,
    DeleteAuth0AccountModal,
    ConfirmDeleteAuth0AccountModal,
    Auth0MigrationFeatureDisabledModal
  },
  mixins: [validationMixin],
  data() {
    return {
      confirmDeleteAccountModalInfo: {},
      deleteAccountModalInfo: {},
      deleteAccountUnableToDeleteModalInfo: {},
      shouldSetModal: false,
      userInfo: {},
      isLoading: true,
      preferredDealerEmail: null
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      user: state => state.account.user,
      apparatuses: state => state.apparatus.apparatuses,
      accountInfo: state => state.account.accountInfo,
      isImpersonating: state => state.auth.isImpersonating,
      impersonatedEmail: state => state.auth.impersonatedEmail,
      currentAppConfig: state => state.application.currentAppConfig
    })
  },
  methods: {
    setShouldSetModal() {
      this.shouldSetModal = !this.shouldSetModal
    },
    openEditAccountModal() {
      this.shouldSetModal = true
      this.openModal('EditAccountInfoModal')
    },
    deleteAccount() {
      if (this.accountProfile && this.getIsDealerManaged()) {
        this.openModal('unable-to-delete-account-modal')
      } else if (this.getIsSubscriptionActiveOrPendingChange() || this.doesAccountHaveLegacySub()) {
        this.deleteAccountUnableToDeleteModalInfo = {
          store: this.subscriptionStore,
          planName: this.subscriptionPlanName,
          hasLegacySub: this.doesAccountHaveLegacySub(),
          legacyPlanName: this.legacyPlanName
        }
        this.openModal('DeleteAccountUnableToDeleteModal')
      } else {
        if (useAuth0AndB2CFeatureFlagEnabled(this.currentAppConfig)) {
          // Should only hit this during b2c migration to auth0
          this.openModal('Auth0MigrationFeatureDisabledModal')
        } else if (useAuth0FeatureFlagEnabled(this.currentAppConfig)) {
          this.openModal('DeleteAuth0AccountModal')
        } else {
          this.deleteAccountModalInfo = {
            orgId: this.accountProfile.organizationId
          }
          this.openModal('DeleteAccountModal')
        }
      }
    },
    closeDeleteAccountModal() {
      document.getElementById('DeleteAccountModal').style.display = 'none'
    },
    closeDeleteAuth0AccountModal() {
      document.getElementById('DeleteAuth0AccountModal').style.display = 'none'
    },
    closeAuth0MigrationFeatureDisabledModal() {
      document.getElementById('Auth0MigrationFeatureDisabledModal').style.display = 'none'
    },
    displayConfirmDeleteAccountModal(orgId) {
      this.confirmDeleteAccountModalInfo = {
        orgId: orgId
      }
      document.getElementById('ConfirmDeleteAccountModal').style.display = 'block'
    },
    displayConfirmDeleteAuth0AccountModal() {
      this.closeDeleteAuth0AccountModal()
      document.getElementById('ConfirmDeleteAuth0AccountModal').style.display = 'block'
    },
    showConfirmDeleteAccountModal(info) {
      this.closeDeleteAccountModal()
      this.displayConfirmDeleteAccountModal(info.orgId)
    },
    onUpdateAccountEmailCLicked() {
      if (useAuth0AndB2CFeatureFlagEnabled(this.currentAppConfig)) {
        // Should only hit this during b2c migration to auth0
        this.openModal('Auth0MigrationFeatureDisabledModal')
      } else {
        updateEmail(this.accountProfile.identityEmail, this.currentAppConfig)
      }
    },
    deleteAccountTitle() {
      return this.isImpersonating ? this.$t('xNotPermittedWhileImpersonating') : ''
    },
    getIsDealerManaged() {
      return this?.apparatuses.some(x => x.isDealerManaged)
    },
    getIsSubscriptionActiveOrPendingChange() {
      if (this.subscriptionDetails?.subscriptions) {
        for (const subscription of this.subscriptionDetails?.subscriptions) {
          this.subscriptionPlanName = subscription.product.name
          // Don't want to delete account if there is an active/pending change sub on the account
          if (
            subscription.status === Constants.SubscriptionDetailActive ||
            subscription.status === Constants.SubscriptionDetailPendingProductChange
          ) {
            this.subscriptionStore = subscription.store
            return true
          }
        }
      }
      return false
    },
    isLegacySub(sub) {
      if (sub.product?.productId && sub.product.productId.indexOf('p-premium') !== -1) return true
      if (sub.productId && sub.productId.indexOf('p-premium') !== -1) return true
      return false
    },
    doesAccountHaveLegacySub() {
      if (this.subscriptionDetails?.legacyBillingProducts?.products) {
        const legacyApparatus = this.subscriptionDetails.legacyBillingProducts.products.find(x => x.productName)
        this.legacyPlanName = legacyApparatus?.productName
        if (this.legacyPlanName !== undefined && legacyApparatus.status === Constants.SubscriptionActive) {
          return true
        }
      }
      return false
    }
  },
  watch: {
    accountProfile: function(newVal, oldVal) {
      if (newVal) {
        this.accountData = _.cloneDeep(this.accountProfile)
      }
    },
    apparatuses: function(newVal, oldVal) {
      if (newVal) {
        if (this.apparatuses.find(x => x.isDealerManaged)) {
          const dealerManagedApparatus = this.apparatuses.find(x => x.isDealerManaged)
          this.preferredDealerEmail = dealerManagedApparatus.preferredDealerEmail
        }
      }
    }
  },
  async mounted() {
    await this.$store.dispatch('getAccountInfo')
    await this.$store.dispatch('getApparatuses')
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
    } else {
      await this.$store.dispatch('getAccountProfile')
    }
    await this.$store
      .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
      .then(response => {
        this.subscriptionDetails = response.data
      })
      .catch(() => {})
    this.isLoading = false
  }
}
</script>
<style scoped>
*.bold-text {
  font-weight: bold;
}
*.btn-outline-danger {
  border: 2px solid;
}
</style>
